import './cat/cat.js';
import './core/state.js';
import './game/battle.js';
import './game/data.js';
import './game/equip.js';
import './game/items.js';
import './game/screens.js';
import './game/ui.js';
import './game/world.js';
import './io/transfer.js';
import './onboarding/tour.js';
import './progress/streak.js';
import './shop/shop.js';
import './social/api.js';
import './social/community.js';
import './social/friends.js';
import './study/editor.js';
import './study/learn.js';
import './ui/confetti.js';
import './ui/modal.js';
import './ui/sidebar.js';
import './ui/views.js';
import './views/calendar.js';
import './views/dashboard.js';
import './views/exams.js';
import './views/stats.js';
import { handleAuthLink, zeigeAnbieter } from './auth/screen.js';
import { CLOUD, cloudMe, markSignedIn } from './auth/account.js';
import { AUTH_DRIN, AUTH_GAST, initPhasen, merkeSitzung, setzeAuthPhase } from './auth/phase.js';
import { checkAchievements } from './progress/xp.js';
import { initDB } from './core/db.js';
import { enterApp, fatal, wireAuth } from './sync/sync.js';
import { guardStorage } from './core/backup.js';
import { initDark, initDesign } from './views/settings.js';
import { loadAll, loadDeco } from './core/load.js';
import { loadUnis } from './social/profile.js';
import './boot/pwa.js';
import { wire } from './boot/wiring.js';

/* ══════════ Start ══════════
   Die Reihenfolge hier entscheidet darüber, ob der erste Eindruck ruhig ist.

   Der Kern: Solange nicht feststeht, ob eine Sitzung besteht, wird WEDER die
   App noch das Anmeldeformular gezeigt. Beides wäre eine Behauptung über
   etwas, das noch niemand weiß – und die Hälfte der Zeit die falsche.

   Das Cookie ist HttpOnly, JavaScript kommt nicht heran; die Antwort auf
   „bin ich angemeldet?" kostet also eine Netzanfrage. Diese Wartezeit ist
   nicht wegzuoptimieren, aber sie muss ehrlich aussehen: Startbildschirm mit
   Platzhalter, dann genau ein Übergang in den richtigen Zustand.

   Wer angemeldet ist, sieht danach sofort das App-Gerüst – mit Skeletons an
   den Stellen, deren Zahlen noch vom Server kommen. Erst wenn der Lernstand
   da ist, springt `data-app` auf „bereit" (siehe enterApp in sync/sync.js). */
(async function init() {
  initPhasen();

  /* Links aus E-Mails:
       ?verify=  bestätigt die Adresse – und meldet dabei gleich an
       ?reset=   öffnet das Formular für ein neues Passwort
       ?newmail= bestätigt eine neue Adresse (nur im angemeldeten Zustand)
     Steht ganz oben, weil es die Abkürzung unten ausschaltet: hier soll ein
     Formular erscheinen, nicht die App-Hülle. */
  const q = new URLSearchParams(location.search);
  const art = q.get('verify') ? 'verify' : q.get('reset') ? 'reset' : q.get('newmail') ? 'newmail' : '';

  /* ── Die Sitzungsfrage zuerst ────────────────────────────────────────────
     Sie stand früher ganz unten, nach initDB(), loadAll(), checkAchievements()
     und der ganzen Verdrahtung. Gemessen: /auth/me antwortet in rund 50 ms –
     der lokale Start davor dauert auf einem vollen Konto fast eine Sekunde.
     Genau so lange stand beim Neuladen der Anmeldebildschirm da, obwohl die
     Antwort längst hätte da sein können.

     Deshalb läuft sie jetzt ab der ersten Zeile, parallel zu allem anderen.
     Der Bildschirm wechselt, sobald sie eintrifft – nicht erst, wenn die
     lokale Arbeit fertig ist.

     Das `.then` hängt sofort dran und nicht erst beim Auswerten: eine
     abgelehnte Zusage ohne Behandlung ist ein unbehandelter Fehler, und das
     ist hier der Normalfall (nicht angemeldet). */
  const sitzung = CLOUD
    ? cloudMe().then((user) => ({ user }), (fehler) => ({ fehler }))
    : Promise.resolve({ fehler: new Error('keine API') });

  /* Sobald „angemeldet" feststeht, sofort die App-Hülle zeigen – auch wenn
     die Datenbank unten noch öffnet. Sichtbar sind dabei nur Skeletons.

     Der Gast-Fall wartet dagegen bis nach wireAuth(): ein Anmeldeformular
     ohne Submit-Handler lädt beim Absenden die Seite neu. Lieber ein paar
     Millisekunden Platzhalter als ein Formular, das etwas anderes tut, als
     es verspricht. */
  if (!art) {
    sitzung.then(({ user }) => {
      if (!user) return;
      markSignedIn(true);
      merkeSitzung(true);
      setzeAuthPhase(AUTH_DRIN);
    });
  }

  try { await initDB(); }
  catch (err) { fatal(err.message || 'Die Datenbank ließ sich nicht öffnen.'); return; }
  await loadAll();
  await checkAchievements(null, true);   // bereits erfüllte Erfolge nachtragen (ohne Getöse, ohne Bonus-XP)
  /* Design und Hell/Dunkel sind bereits vor dem ersten Farbauftrag gesetzt
     (kleines Skript in index.html). Hier geht es nur noch darum, den
     Umschalter in den Einstellungen anzuschließen und auf Systemwechsel zu
     hören – die Klassen selbst liegen längst am body. */
  initDark();
  initDesign();
  wire();
  wireAuth();
  loadDeco();
  loadUnis();
  guardStorage();

  // Ohne API gibt es keine Anmeldung mehr – das frühere lokale Konto-System
  // ist entfernt. Lieber ehrlich abbrechen als so tun, als wäre man drin.
  if (!CLOUD) {
    fatal('Diese Installation hat keine API-Adresse konfiguriert (config.js). Ohne sie ist keine Anmeldung möglich.');
    return;
  }

  /* In den Gast-Zustand wechseln: Anmeldeformular zeigen und die Anbieter
     nachladen. Die Anbieter erst hier zu holen ist Absicht – wer angemeldet
     ist, braucht kein Google-Skript und keine Anbieter-Abfrage. */
  const alsGast = async () => {
    markSignedIn(false);
    merkeSitzung(false);   // Vermutung war falsch (oder es gab nie eine)
    setzeAuthPhase(AUTH_GAST);
    await zeigeAnbieter();
  };

  if (art) {
    history.replaceState({}, '', location.pathname);   // Token aus der URL nehmen

    /* Der Adresswechsel geht nur im angemeldeten Zustand – dafür muss die
       Antwort auf die Sitzungsfrage hier vorliegen. Vorher lief dieser Zweig
       VOR der Anfrage: `hasSession()` war immer false, und der Link aus der
       Mail endete zuverlässig bei „Melde dich zuerst an". */
    const { user } = await sitzung;
    if (user) markSignedIn(true);

    const fertig = await handleAuthLink(art, q.get(art));
    if (fertig) return;                                 // hat direkt angemeldet

    /* Nach bestätigtem Adresswechsel gehört man in die App, nicht auf den
       Anmeldebildschirm. Das Konto einmal frisch holen, sonst stünde oben
       weiter die alte Adresse. */
    if (art === 'newmail' && user) {
      const frisch = await cloudMe().catch(() => user);
      merkeSitzung(true);
      setzeAuthPhase(AUTH_DRIN);
      await enterApp(frisch);
      return;
    }
    await alsGast();
    return;
  }

  /* Die Antwort von ganz oben einsammeln. Sie liegt hier in aller Regel
     längst vor – die Hülle steht dann schon, und dieses `await` kostet
     nichts. */
  const { user } = await sitzung;
  if (user) {
    await enterApp(user);          // holt den Lernstand und schaltet auf „bereit"
    return;
  }
  await alsGast();
})();
